import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService, Translation } from '@ngneat/transloco';
import { HdContentLanguageService } from './hd-content-language.service';
import { HdCircuitInfoVariant } from './circuit-info/hd-circuit-info-variant';

/** Transloco scope-path waar de circuit-translations leven. */
const HD_CIRCUITS_SCOPE = 'human-design/circuits';

/** Output-pad van de via `angular.json` als asset gekopieerde markdown-bestanden. */
const HD_CIRCUITS_ASSET_BASE = '/assets/docs/human-design/circuits';

/** Talen waarvoor circuit-markdown-assets bestaan; andere talen vallen terug op `nl`. */
const HD_CIRCUITS_MARKDOWN_LANGS = new Set(['nl', 'en']);

/**
 * Vertaalde info per circuit: korte labels (naam, type, Engelse titel) +
 * `kernwoorden`-array. De prosa zelf staat NIET in de translations — die
 * wordt via {@link HdCircuitInfoService.markdownUrl} als asset geladen.
 */
export interface HdCircuitTranslation {
  readonly name: string;
  readonly shortName: string;
  readonly titleEn: string;
  readonly type: string;
  readonly kernwoorden: readonly string[];
}

/** Vertaalde info voor het algemene circuits-overzicht. */
export interface HdCircuitsOverviewTranslation {
  readonly title: string;
  readonly subtitle: string;
  readonly kernwoorden: readonly string[];
}

/** UI-labels van het circuit-info paneel (variant-switcher, knoppen, kernwoorden-titel, …). */
export interface HdCircuitInfoUiLabels {
  readonly openCircuitInfo: string;
  readonly openOverviewInfo: string;
  readonly variantCompact: string;
  readonly variantNormal: string;
  readonly variantExtended: string;
  readonly variantSwitcherLabel: string;
  readonly kernwoordenLabel: string;
  readonly loadingContent: string;
  readonly loadError: string;
}

const EMPTY_CIRCUIT: HdCircuitTranslation = {
  name: '',
  shortName: '',
  titleEn: '',
  type: '',
  kernwoorden: [],
};

const EMPTY_OVERVIEW: HdCircuitsOverviewTranslation = {
  title: '',
  subtitle: '',
  kernwoorden: [],
};

const EMPTY_UI: HdCircuitInfoUiLabels = {
  openCircuitInfo: '',
  openOverviewInfo: '',
  variantCompact: 'Compact',
  variantNormal: 'Normaal',
  variantExtended: 'Uitgebreid',
  variantSwitcherLabel: '',
  kernwoordenLabel: 'Kernwoorden',
  loadingContent: 'Loading…',
  loadError: 'Could not load content.',
};

/**
 * Reactieve facade voor alles wat de circuit-info-componenten nodig hebben:
 * vertaalde labels (naam, type, Engelse titel, kernwoorden, UI-strings) én
 * de URL naar het markdown-asset voor een gegeven `(circuitKey, variant)`-
 * combinatie. Spiegelt het patroon van {@link HdGateInfoService} maar dan
 * sterk vereenvoudigd: geen per-poort metadata, geen lijn-info — alleen
 * datgene wat de drie circuit-info-componenten nodig hebben.
 *
 * **Markdown-bestanden** worden via {@link markdownUrl} opgevraagd; de
 * `<markdown [src]="…">`-component van `ngx-markdown` doet het laden zelf.
 */
@Injectable({ providedIn: 'root' })
export class HdCircuitInfoService {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Actieve app-taal als signaal — drijft de reactieve label-signals. */
  private readonly activeLang: Signal<string>;

  /** Toename triggert herlees van scope-translations na (lazy) load. */
  private readonly scopesLoadCounter = signal(0);

  /** Set van `scope/lang`-combinaties waarvoor de load al is aangevraagd. */
  private readonly requestedScopes = new Set<string>();

  constructor() {
    this.activeLang = toSignal(this.transloco.langChanges$, {
      initialValue: this.transloco.getActiveLang(),
    });

    effect(() => {
      const lang = this.activeLang();
      this.ensureScopeLoaded(HD_CIRCUITS_SCOPE, lang);
    });
  }

  // -------------------------------------------------------------------------
  // Reactieve lookup-signals (actieve app-taal)
  // -------------------------------------------------------------------------

  /** Vertaalde circuit-info per app-key (actieve app-taal). */
  readonly circuitTranslations: Signal<Readonly<Record<string, HdCircuitTranslation>>> = computed(
    () => this.circuitTranslationsIn(this.activeLang()),
  );

  /** Vertaalde overview-info (actieve app-taal). */
  readonly overviewTranslation: Signal<HdCircuitsOverviewTranslation> = computed(
    () => this.overviewTranslationIn(this.activeLang()),
  );

  /** UI-labels voor de info-panelen (actieve app-taal). */
  readonly uiLabels: Signal<HdCircuitInfoUiLabels> = computed(
    () => this.uiLabelsIn(this.activeLang()),
  );

  /** Vertaling per circuit, of een lege fallback wanneer onbekend. */
  circuitTranslation(circuitKey: string): HdCircuitTranslation {
    return this.circuitTranslations()[circuitKey] ?? EMPTY_CIRCUIT;
  }

  // -------------------------------------------------------------------------
  // Lang-geparametriseerde varianten (voor content-language-override consumers)
  // -------------------------------------------------------------------------

  circuitTranslationsIn(lang: string): Readonly<Record<string, HdCircuitTranslation>> {
    const scope = this.getScopeTranslation(HD_CIRCUITS_SCOPE, lang);
    return readCircuitTranslationsFrom(scope);
  }

  overviewTranslationIn(lang: string): HdCircuitsOverviewTranslation {
    const scope = this.getScopeTranslation(HD_CIRCUITS_SCOPE, lang);
    return readOverviewFrom(scope);
  }

  uiLabelsIn(lang: string): HdCircuitInfoUiLabels {
    const scope = this.getScopeTranslation(HD_CIRCUITS_SCOPE, lang);
    return readUiLabelsFrom(scope);
  }

  // -------------------------------------------------------------------------
  // Markdown-asset URL
  // -------------------------------------------------------------------------

  /**
   * URL naar het markdown-bestand voor een `(circuitKey, variant)`-combinatie.
   * Wordt door de `<markdown [src]="…">`-component van ngx-markdown geladen
   * (lazy fetch over HTTP). Default-taal is de content-taal (chart-override),
   * met fallback op de actieve app-taal.
   *
   * Engelse markdown-bestanden bestaan momenteel **nog niet** — wanneer de
   * gevraagde `lang` geen overeenstemmende map heeft, valt het pad terug op
   * de Nederlandse versie. Zodra `en/`-bestanden toegevoegd worden, verandert
   * de fallback automatisch mee (geen code-wijziging nodig).
   */
  markdownUrl(circuitKey: string, variant: HdCircuitInfoVariant, lang?: string): string {
    const resolved = this.resolveMarkdownLang(lang ?? this.contentLang.effectiveLang());
    return `${HD_CIRCUITS_ASSET_BASE}/${resolved}/${circuitKey}_${variant}.md`;
  }

  /**
   * URL naar het algemene overzichtsdocument (README_extended). Variant wordt
   * voorlopig genegeerd — er is één algemeen overzichtsbestand. Latere
   * uitbreiding (overview_compact.md / overview_normal.md / overview_extended.md)
   * kan dit zonder breaking change uitbreiden.
   */
  overviewMarkdownUrl(_variant: HdCircuitInfoVariant = 'normal', lang?: string): string {
    const resolved = this.resolveMarkdownLang(lang ?? this.contentLang.effectiveLang());
    return `${HD_CIRCUITS_ASSET_BASE}/${resolved}/README_extended.md`;
  }

  /**
   * Momenteel bestaan enkel Nederlandse markdown-bestanden onder
   * `assets/docs/human-design/circuits/nl/`. Alles wat geen `nl` is, valt
   * terug op `nl` — vervangen zodra `en/`-content gegenereerd wordt.
   */
  private resolveMarkdownLang(lang: string): string {
    const normalized = (lang || '').toLowerCase().split('-')[0];
    return HD_CIRCUITS_MARKDOWN_LANGS.has(normalized) ? normalized : 'nl';
  }

  // -------------------------------------------------------------------------
  // Scope-laad helpers
  // -------------------------------------------------------------------------

  /**
   * Vraagt de gegeven scope/lang-combinatie eenmalig (lazy) bij Transloco
   * op. Bij eerste binnenkomst markeren we de combinatie als requested en
   * verhogen we {@link scopesLoadCounter} zodra de load resolvet, zodat
   * downstream-`computed()`-graphs opnieuw uitlezen.
   */
  private ensureScopeLoaded(scope: string, lang: string): void {
    const key = `${scope}/${lang}`;
    if (this.requestedScopes.has(key)) return;
    this.requestedScopes.add(key);
    this.transloco.load(`${scope}/${lang}`).subscribe({
      next: () => this.scopesLoadCounter.update(value => value + 1),
      error: () => this.requestedScopes.delete(key),
    });
  }

  /**
   * Synchrone read van de translation-tree voor een scope/lang. Triggert
   * geen load — gebruik {@link ensureScopeLoaded} of de constructor-effect
   * om te laden vóór de eerste read. De load-counter wordt gelezen zodat
   * deze methode reactief is voor downstream-`computed()`-graphs.
   */
  private getScopeTranslation(scope: string, lang: string): Translation {
    this.scopesLoadCounter();
    this.ensureScopeLoaded(scope, lang);
    return this.transloco.getTranslation(`${scope}/${lang}`) ?? {};
  }
}

// ---------------------------------------------------------------------------
// Pure read-functies (gebruikt door zowel signal-getters als lang-overrides).
// Defensief — een ontbrekende sleutel valt terug op de lege placeholder zodat
// de UI nooit `undefined` ziet.
// ---------------------------------------------------------------------------

function readCircuitTranslationsFrom(translation: Translation): Readonly<Record<string, HdCircuitTranslation>> {
  const circuits = (translation['circuits'] ?? {}) as Record<string, Partial<HdCircuitTranslation>>;
  const result: Record<string, HdCircuitTranslation> = {};
  for (const [key, value] of Object.entries(circuits)) {
    result[key] = {
      name: value.name ?? '',
      shortName: value.shortName ?? value.name ?? '',
      titleEn: value.titleEn ?? '',
      type: value.type ?? '',
      kernwoorden: Array.isArray(value.kernwoorden) ? value.kernwoorden : [],
    };
  }
  return result;
}

function readOverviewFrom(translation: Translation): HdCircuitsOverviewTranslation {
  const overview = (translation['overview'] ?? {}) as Partial<HdCircuitsOverviewTranslation>;
  return {
    title: overview.title ?? '',
    subtitle: overview.subtitle ?? '',
    kernwoorden: Array.isArray(overview.kernwoorden) ? overview.kernwoorden : [],
  };
}

function readUiLabelsFrom(translation: Translation): HdCircuitInfoUiLabels {
  const ui = ((translation['info'] ?? {}) as { ui?: Partial<HdCircuitInfoUiLabels> }).ui ?? {};
  return {
    openCircuitInfo: ui.openCircuitInfo ?? EMPTY_UI.openCircuitInfo,
    openOverviewInfo: ui.openOverviewInfo ?? EMPTY_UI.openOverviewInfo,
    variantCompact: ui.variantCompact ?? EMPTY_UI.variantCompact,
    variantNormal: ui.variantNormal ?? EMPTY_UI.variantNormal,
    variantExtended: ui.variantExtended ?? EMPTY_UI.variantExtended,
    variantSwitcherLabel: ui.variantSwitcherLabel ?? EMPTY_UI.variantSwitcherLabel,
    kernwoordenLabel: ui.kernwoordenLabel ?? EMPTY_UI.kernwoordenLabel,
    loadingContent: ui.loadingContent ?? EMPTY_UI.loadingContent,
    loadError: ui.loadError ?? EMPTY_UI.loadError,
  };
}
